import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { deleteFund, listFunds, type Fund } from "@/lib/api/funds";
import { useRole } from "@/lib/auth/use-role";

const fmtUsd = (n: number | null): string =>
  n === null ? "—" : `$${(n / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 0 })}M`;

function FundsListInner() {
  const tenant = useTenant();
  const qc = useQueryClient();
  const { canWrite } = useRole(tenant.id);
  const {
    data: funds,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteFund(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["funds", tenant.id] }),
  });

  function onDelete(f: Fund): void {
    const ok = window.confirm(
      `Delete ${f.name}? This also deletes its LP commitments. This cannot be undone.`,
    );
    if (!ok) return;
    del.mutate(f.id);
  }

  return (
    <>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Funds</h1>
          <p className="text-sm text-neutral-400">
            Funds in <span className="text-neutral-200">{tenant.name}</span>.
          </p>
        </div>
        {canWrite ? (
          <Link
            to="/t/$slug/funds/new"
            params={{ slug: tenant.slug }}
            className="rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white"
          >
            New fund
          </Link>
        ) : null}
      </header>

      {del.error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {del.error.message}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading funds…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : !funds || funds.length === 0 ? (
        <div className="rounded border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-400">
          <p className="font-medium text-neutral-100">No funds yet</p>
          <p className="mt-1">
            {canWrite
              ? "Create your first fund to start tracking commitments."
              : "Your tenant has no funds yet. Ask an owner or admin to add one."}
          </p>
          {canWrite ? (
            <Link
              to="/t/$slug/funds/new"
              params={{ slug: tenant.slug }}
              className="mt-3 inline-block rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white"
            >
              Create fund
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-neutral-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-900/50 text-[11px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Vintage</th>
                <th className="px-3 py-2">Strategy</th>
                <th className="px-3 py-2 text-right">Size</th>
                <th className="px-3 py-2">Status</th>
                {canWrite ? <th className="px-3 py-2 text-right">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {funds.map((f) => (
                <tr key={f.id} className="hover:bg-neutral-900/50">
                  <td className="px-3 py-2.5 text-neutral-100">{f.name}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{f.vintage_year ?? "—"}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{f.strategy ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right text-neutral-400">
                    {fmtUsd(f.fund_size_usd)}
                  </td>
                  <td className="px-3 py-2.5 text-neutral-400">{f.status}</td>
                  {canWrite ? (
                    <td className="px-3 py-2.5 text-right text-xs">
                      <Link
                        to="/t/$slug/funds/$fundId/edit"
                        params={{ slug: tenant.slug, fundId: f.id }}
                        className="text-neutral-300 hover:text-neutral-100"
                      >
                        Edit
                      </Link>
                      <button
                        type="button"
                        onClick={() => onDelete(f)}
                        disabled={del.isPending}
                        className="ml-3 text-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function FundsListPage() {
  return (
    <TenantLayout>
      <FundsListInner />
    </TenantLayout>
  );
}
